import { useState, useEffect } from 'react';
import { useLanguage } from './useLanguage';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || '';

/**
 * Die Seiten, für die die SEO-Verwaltung Titel und Beschreibung pflegt —
 * dieselbe Liste wie `valid_keys` in `GET /api/public/seo/{page_key}`
 * (`routers/content_seo.py`). Hard-Split: 'news' lebt in paperball.news.
 * `landing` ist seit 30.09.2026 die Startseite für Gäste (`StartPage`),
 * `landing_monte_carlo` seit 02.10.2026 die Monte-Carlo-Seite (`MonteCarloLandingPage`),
 * `landing_backtest` die Backtest-Seite (`BacktestLandingPage`),
 * `landing_entnahmeplan` seit 07.10.2026 die Seite zum Entnahmeplan (`EntnahmeplanLandingPage`),
 * `landing_berater` seit 07.10.2026 die Seite für Finanzberater (`BeraterLandingPage`),
 * `landing_watchlist` seit 07.10.2026 die Watchlist-Seite (`WatchlistLandingPage`),
 * `landing_depot_check` und `landing_fire` seit 07.10.2026 (`DepotCheckLandingPage`, `FireLandingPage`).
 */
export type SeoSeite = 'landing' | 'landing_backtest' | 'landing_monte_carlo' | 'landing_entnahmeplan' | 'landing_berater' | 'landing_watchlist' | 'landing_depot_check' | 'landing_fire' | 'landing_av_depot' | 'landing_vorabpauschale' | 'leaderboard' | 'leaderboard_portfolios' | 'leaderboard_architects' | 'pricing';

interface SeoDaten {
  metaTitle: string | null;
  metaDescription: string | null;
  isLoading: boolean;
}

export interface SeoAntwort {
  meta_title: string | null;
  meta_description: string | null;
}

/**
 * Titel und Beschreibung, die eine Server-Seite (Next.js-Phase 2) schon
 * geholt hat — je Seite und Sprache. Der Hook startet damit, statt erst nach
 * seinem Abruf: Sonst trüge das Server-HTML den Vorgabetext und der Browser
 * tauschte ihn danach gegen den gepflegten aus — der Crawler läse einen
 * anderen Titel als der Besucher.
 */
const vorgelegt = new Map<string, SeoAntwort>();

export function legeSeoVor(pageKey: SeoSeite, language: string, daten: SeoAntwort): void {
  vorgelegt.set(`${pageKey}|${language}`, daten);
}

/**
 * Hook zum Laden von SEO Meta-Tags für eine Seite
 *
 * @param pageKey - Schlüssel der Seite
 * @returns SEO Daten und Ladezustand
 */
export function useSeo(pageKey: SeoSeite) {
  const { language } = useLanguage();
  const [seoData, setSeoData] = useState<SeoDaten>(() => {
    const daten = vorgelegt.get(`${pageKey}|${language || 'de'}`);
    return daten
      ? { metaTitle: daten.meta_title || null, metaDescription: daten.meta_description || null, isLoading: false }
      : { metaTitle: null, metaDescription: null, isLoading: true };
  });

  useEffect(() => {
    const loadSeo = async () => {
      try {
        const lang = language || 'de';
        const response = await fetch(`${API_BASE}/api/public/seo/${pageKey}?lang=${lang}`);

        if (response.ok) {
          const data: SeoAntwort = await response.json();
          setSeoData({
            metaTitle: data.meta_title || null,
            metaDescription: data.meta_description || null,
            isLoading: false
          });
        } else {
          setSeoData(prev => ({ ...prev, isLoading: false }));
        }
      } catch (err) {
        console.error('Failed to load SEO data:', err);
        setSeoData(prev => ({ ...prev, isLoading: false }));
      }
    };

    if (pageKey) {
      loadSeo();
    }
  }, [pageKey, language]);

  // Kein Markenzusatz im Titel (seit 25.10.2026): Google zeigt den Seitennamen
  // „Paperball Finance“ (`WebSite.name`) über dem Snippet. Jede Seite setzt ihren
  // Titel deshalb selbst aus `metaTitle || t('….meta.title', …)`.
  return seoData;
}

export default useSeo;
